import { motion } from "framer-motion";

/**
 * Animated architectural SVG mark for the hero + preloader.
 * Distinct from the raster logo used in nav/footer.
 *
 * Animation choreography:
 *  1. Ground baseline draws left-to-right.
 *  2. Four tower silhouettes stroke themselves upward (staggered).
 *  3. A gold ember rises from the central spire on loop.
 *  4. A shimmer sweep passes across the entire mark on loop.
 *  5. The whole mark gently breathes (subtle scale).
 */
export default function LogoAnimatedSvg({ size = 220, className = "" }) {
    const towers = [
        "M22 108 L22 42 L44 22 L44 108",
        "M44 108 L44 22 L60 38 L60 108",
        "M60 108 L60 38 L76 22 L76 108",
        "M76 108 L76 22 L98 42 L98 108",
    ];

    return (
        <motion.div
            className={`relative inline-block ${className}`}
            style={{ width: size, height: size }}
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
        >
            <svg
                viewBox="0 0 120 120"
                width={size}
                height={size}
                className="block relative z-[1]"
                aria-label="BRA Sheriff animated logo mark"
            >
                <defs>
                    <linearGradient id="lag-gold" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#E2C56D" />
                        <stop offset="55%" stopColor="#C89B3C" />
                        <stop offset="100%" stopColor="#8a6a26" />
                    </linearGradient>
                    <linearGradient id="lag-sheen" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#C89B3C" stopOpacity="0" />
                        <stop offset="50%" stopColor="#fff2b0" stopOpacity="1" />
                        <stop offset="100%" stopColor="#C89B3C" stopOpacity="0" />
                    </linearGradient>
                    <filter id="lag-glow">
                        <feGaussianBlur stdDeviation="1.6" result="b" />
                        <feMerge>
                            <feMergeNode in="b" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <mask id="lag-mask">
                        <g fill="none" stroke="#fff" strokeWidth="5" strokeLinejoin="miter">
                            {towers.map((d, i) => (
                                <path key={i} d={d} />
                            ))}
                            <line x1="22" y1="108" x2="98" y2="108" />
                        </g>
                    </mask>
                </defs>

                {/* Baseline draws first */}
                <motion.line
                    x1="22"
                    y1="108"
                    x2="98"
                    y2="108"
                    stroke="url(#lag-gold)"
                    strokeWidth="3"
                    strokeLinecap="square"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
                />

                {/* Tower silhouettes stroke upward */}
                <g
                    fill="none"
                    stroke="url(#lag-gold)"
                    strokeWidth="3"
                    strokeLinejoin="miter"
                    strokeLinecap="square"
                    filter="url(#lag-glow)"
                >
                    {towers.map((d, i) => (
                        <motion.path
                            key={i}
                            d={d}
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: 1, opacity: 1 }}
                            transition={{
                                duration: 1.5,
                                delay: 0.55 + i * 0.22,
                                ease: [0.2, 0.7, 0.2, 1],
                            }}
                        />
                    ))}
                </g>

                {/* Small notch at the spire peak (blueprint marker) */}
                <motion.circle
                    cx="60"
                    cy="38"
                    r="2.4"
                    fill="#E2C56D"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{
                        opacity: [0, 1, 1, 0],
                        scale: [0, 1, 1.4, 2],
                    }}
                    transition={{
                        duration: 2.6,
                        delay: 2.1,
                        repeat: Infinity,
                        repeatDelay: 2.4,
                        ease: "easeOut",
                    }}
                />

                {/* Rising ember from the spire */}
                <motion.circle
                    cx="60"
                    cy="38"
                    r="1.6"
                    fill="#fff2b0"
                    initial={{ opacity: 0, y: 0 }}
                    animate={{
                        opacity: [0, 1, 0],
                        y: [-2, -22, -34],
                    }}
                    transition={{
                        duration: 2.4,
                        delay: 2.2,
                        repeat: Infinity,
                        repeatDelay: 1.6,
                        ease: "easeOut",
                    }}
                />

                {/* Ambient dashed foundation lines under the base */}
                <g stroke="rgba(212,175,55,0.35)" strokeWidth="0.6" strokeDasharray="1 3">
                    <motion.line
                        x1="14"
                        y1="112"
                        x2="106"
                        y2="112"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 1.2, delay: 1.6 }}
                    />
                    <motion.line
                        x1="20"
                        y1="115"
                        x2="100"
                        y2="115"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 1.2, delay: 1.8 }}
                    />
                </g>

                {/* Shimmer sweep masked to the mark */}
                <g mask="url(#lag-mask)">
                    <motion.rect
                        y="0"
                        width="40"
                        height="120"
                        fill="url(#lag-sheen)"
                        opacity="0.9"
                        initial={{ x: -50 }}
                        animate={{ x: 140 }}
                        transition={{
                            duration: 2.4,
                            delay: 2.0,
                            repeat: Infinity,
                            repeatDelay: 3.4,
                            ease: "easeInOut",
                        }}
                    />
                </g>
            </svg>

            {/* Soft radial glow behind the mark */}
            <motion.div
                aria-hidden
                className="absolute inset-0 -z-10 rounded-full"
                style={{
                    background:
                        "radial-gradient(closest-side, rgba(200,155,60,0.28), rgba(200,155,60,0) 70%)",
                    filter: "blur(18px)",
                }}
                animate={{ opacity: [0.5, 0.9, 0.5], scale: [0.9, 1.05, 0.9] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            />
        </motion.div>
    );
}
